//  Copyright (c) 2018 Cigent Technology Inc. All Rights Reserved.

import {Component, OnDestroy, OnInit} from '@angular/core';
import {SecurityRequestService} from '../../../../services/security-request.service';
import {BehaviorSubject, forkJoin, of} from 'rxjs';
import {catchError, switchMap, tap} from 'rxjs/operators';
import {
    ISecurityRequestResponse,
    ISecurityRequestToApprove,
    SecurityRequestResult,
    SecurityRequestStatus,
    SecurityRequestType
} from '../../../../types/security-requests';
import {RestConfigService} from '../../../../services/config-service';
import {IRestConfig} from '../../../../types/config';
import {TranslateService} from '@ngx-translate/core';
import {NotificationService} from '../../../../services/notification-service';
import authHelper from '../../../auth/authHelper';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';

@UntilDestroy()

@Component({
    selector: 'app-security-approval-banner',
    templateUrl: './security-approval-banner.component.html',
    styleUrls: ['./security-approval-banner.component.scss'],
    standalone: false
})
export class SecurityApprovalBannerComponent implements OnInit, OnDestroy {
    found: BehaviorSubject<boolean>;
    toApprove: ISecurityRequestToApprove;
    currentRequest: ISecurityRequestResponse;
    acceptedMinutesRemaining: number;
    restConfig: IRestConfig;
    intervalId: number;

    readonly SecurityRequestType = SecurityRequestType;
    readonly Result = SecurityRequestResult;
    readonly Status = SecurityRequestStatus;

    constructor(private readonly securityRequestService: SecurityRequestService,
                private readonly configService: RestConfigService,
                private readonly translateService: TranslateService,
                private readonly notificationService: NotificationService) {
        this.found = securityRequestService.found$;
    }

    ngOnInit(): void {
        this.getRequests();
        this.intervalId = window.setInterval(() => {
            this.securityRequestService.refresh$.next(null);
        }, 60000);
    }

    ngOnDestroy() {
        clearTimeout(this.intervalId);
    }

    setActiveMinutes(dateString) {
        const then = (new Date(dateString)).valueOf();
        const now = (new Date()).valueOf();
        const duration = this.restConfig.recovery_key_access_duration_minutes;
        this.acceptedMinutesRemaining = duration - Math.round((now - then) / 1000 / 60);
    }

    getRequests() {
        if (!authHelper.isPasswordExpired()) {
            this.securityRequestService.refresh$.pipe(
                switchMap(() => {
                    if (!this.restConfig) {
                        return this.configService.getRestConfig().pipe(
                            tap(config => {
                                this.restConfig = config;
                            })
                        );
                    }
                    return of(null);
                }),
                switchMap(() => {
                    return forkJoin({
                        toApprove: this.securityRequestService.getRequestToApprove(),
                        currentRequest: this.securityRequestService.getCurrentRequest(),
                    });
                }),
                catchError(error => {
                    console.error('SecurityRequests status error', error);
                    return of(null);
                }),
                untilDestroyed(this)
            ).subscribe(response => {
                if(!response) {
                    return;
                }
                this.toApprove = response.toApprove;
                this.currentRequest = response.currentRequest;
                if (this.toApprove?.found ||
                    this.currentRequest.status == SecurityRequestStatus.APPROVED ||
                    this.currentRequest.status == SecurityRequestStatus.ACCEPTED) {
                    if (this.currentRequest.status == SecurityRequestStatus.ACCEPTED) {
                        this.setActiveMinutes(this.currentRequest.accepted);
                    }
                    this.found.next(true);
                } else {
                    this.found.next(false);
                }
            });
        }
    }

    postRequestStatus(id: number, result: SecurityRequestResult) {
        this.securityRequestService.postSecurityStatus(id, result).subscribe(response => {
            if (response.alreadyApproved) {
                const title = this.translateService.instant('SECURITY_REQUEST_ALREADY_APPROVED_TITLE');
                const text = this.translateService.instant('SECURITY_REQUEST_ALREADY_APPROVED_TEXT');
                this.notificationService.showError(text, title);
            }
            this.securityRequestService.refresh$.next(null);
        });
    }
}
